'use client';

import React from 'react';

import {
  Bell,
  CircleUserIcon,
  InfoIcon,
  LogOut,
  QrCodeIcon,
  SettingsIcon,
} from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import { CaretSortIcon } from '@radix-ui/react-icons';
import { useAuthActions, useAuthState } from '@/utils/auth';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { UserQR } from './UserQR';
import { useMakeFilteredUrl } from '@/hooks/useFilter';
import NotificationSheet, {
  useToggleNotificationSheet,
  useUnreadNotificationCount,
} from './Layout/NotificationMenu';
import { Badge } from './ui/badge';
import { NavLink } from 'react-router-dom';
import { getAppReleaseShort } from '@/utils/appRelease';

export const NavUser = () => {
  const { isMobile } = useSidebar();
  const { user } = useAuthState();
  const make = useMakeFilteredUrl();
  const { signOut } = useAuthActions();
  const toggleNotificationSheet = useToggleNotificationSheet();
  const unreadNotificationCount = useUnreadNotificationCount();
  const [infoModalOpen, setInfoModalOpen] = React.useState(false);
  const [QRModalOpen, setQRModalOpen] = React.useState(false);
  const initials = `${user?.first_name} ${user?.last_name}`
    .split(' ')
    .map((n) => n[0]?.toUpperCase())
    .join('');

  return (
    <>
      <NotificationSheet side="left" />
      <Dialog open={infoModalOpen} onOpenChange={setInfoModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Información de Desarrollo</DialogTitle>
          </DialogHeader>
          Bilderit - Version {import.meta.env.VITE_APP_BRANCH}@
          {getAppReleaseShort()}
        </DialogContent>
      </Dialog>
      <Dialog open={QRModalOpen} onOpenChange={setQRModalOpen}>
        <DialogContent className="items-center justify-center">
          <DialogHeader>
            <DialogTitle>Mi Código QR</DialogTitle>
          </DialogHeader>
          <UserQR />
        </DialogContent>
      </Dialog>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="relative data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground overflow-visible"
              >
                {!!unreadNotificationCount && (
                  <Badge
                    variant="destructive"
                    className="absolute right-0 top-0 translate-x-1/2 -translate-y-1/2 py-0.5 px-1"
                  >
                    {unreadNotificationCount}
                  </Badge>
                )}
                <Avatar className="size-8 rounded-lg">
                  <AvatarImage
                    src={(user as any)?.avatar}
                    alt={`${user?.first_name} ${user?.last_name}`}
                  />
                  <AvatarFallback className="rounded-lg">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {user?.first_name} {user?.last_name}
                  </span>
                  <span className="truncate text-xs">{user?.email}</span>
                </div>
                <CaretSortIcon className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              side={isMobile ? 'bottom' : 'right'}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  <Avatar className="size-8 rounded-lg">
                    <AvatarImage
                      src={(user as any)?.avatar}
                      alt={`${user?.first_name} ${user?.last_name}`}
                    />
                    <AvatarFallback className="rounded-lg">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">
                      {user?.first_name} {user?.last_name}
                    </span>
                    <span className="truncate text-xs">{user?.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuGroup>
                <DropdownMenuItem asChild>
                  <NavLink to={make('/profile')}>
                    <CircleUserIcon />
                    Perfil
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setQRModalOpen(true)}>
                  <QrCodeIcon />
                  Mi Código QR
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <NavLink to={make('/settings')}>
                    <SettingsIcon />
                    Configuración
                  </NavLink>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setInfoModalOpen(true)}>
                  <InfoIcon />
                  Información
                </DropdownMenuItem>
                <DropdownMenuItem onClick={toggleNotificationSheet}>
                  <Bell />
                  Notificaciones{' '}
                  {!!unreadNotificationCount && (
                    <Badge
                      variant="destructive"
                      className="ml-auto py-0.5 px-1"
                    >
                      {unreadNotificationCount}
                    </Badge>
                  )}
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => signOut()}>
                <LogOut />
                Salir
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
    </>
  );
};
